import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useTable, CellProps, Column, Cell, HeaderGroup, Row } from 'react-table'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'

import PersonAdd from '@mui/icons-material/PersonAdd'

import DelayedRender from '@/components/hoc/DelayedRender'

import colors from '@/assets/scss/_themes-vars.module.scss'

export type MedecinData = {
  name: string
  surname: string
  gender?: 'M' | 'F' | 'I' | 'O'
  city?: string
  cps?: string
  email?: string
}

export type AddMedecinTableProps = {
  /**
   * If `true`, the first row is selected by default
   */
  autoHightlight?: boolean
  data: MedecinData[]
  loading?: boolean
  onSelect?: (data: MedecinData) => void
  onAdd: (data: MedecinData) => void
}

const rowHeight = 60

export function AddMedecinTable(props: AddMedecinTableProps) {
  const { data, loading, onSelect, onAdd } = props
  const [selectedRow, setSelectedRow] = useState<string>()
  const { t } = useTranslation()

  const columns = useMemo(() => {
    return [
      {
        Header: t('page.staffs.staffsTable.header.surname'),
        accessor: 'surname',
        Cell: ({ value: lastname }: CellProps<MedecinData, string>) => (
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '15px' }}>{lastname}</Typography>
        )
      },
      {
        Header: t('page.staffs.staffsTable.header.name'),
        accessor: 'name',
        Cell: ({ value: name }: CellProps<MedecinData, string>) => (
          <Typography variant="body2" sx={{ color: colors.gray600, fontSize: '15px' }}>{name}</Typography>
        )
      },
      {
        Header: t('page.staffs.staffsTable.header.speciality'),
        accessor: 'speciality'
      },
      {
        Header: t('page.staffs.staffsTable.header.city'),
        accessor: 'city'
      },
      {
        Header: t('page.staffs.staffsTable.header.cps'),
        accessor: 'cps'
      }
    ] as Column<MedecinData>[]
  }, [t])

  const {
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows
  } = useTable<MedecinData>(
    {
      columns,
      data
    }
  )


  const handleSelect = (row: Row<MedecinData>) => {
    setSelectedRow(row.original.cps)
    onSelect?.(row.original)
  }

  return (
    <Table stickyHeader sx={{ minWidth: 650 }} aria-label="simple table">
      <TableHead sx={{ bgcolor: colors.gray100 }}>
        {headerGroups.map((headerGroup: HeaderGroup<MedecinData>) => (
          <TableRow {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map((column: HeaderGroup<MedecinData>) => {
              return (
                <TableCell
                  {...column.getHeaderProps()}
                  scope="col"
                  sx={{
                    color: colors.gray600,
                    fontSize: '12px',
                    fontWeight: 500,
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase'
                  }}
                >
                  {column.render('Header')}
                </TableCell>
              )
            })}
            <TableCell align="right">{t('common:table.header.action')}</TableCell>
          </TableRow>
        ))}
      </TableHead>
      <TableBody {...getTableBodyProps()} sx={{ position: 'relative' }}>
        {loading && (
          <DelayedRender delay={300}>
            <TableRow>
              <TableCell sx={{ border: 'none', padding: 0 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: 'rgba(255, 255, 255, 0.7)'
                  }}
                >
                  <CircularProgress size={20} />
                </Box>
              </TableCell>
            </TableRow>
          </DelayedRender>
        )}
        {rows.map((row) => {
          prepareRow(row)
          return (
            <TableRow
              {...row.getRowProps()}
              onClick={() => handleSelect(row)}
              selected={selectedRow === row.original.cps}
              sx={{ cursor: 'pointer' }}
            >
              {row.cells.map((cell: Cell<MedecinData>) => (
                <TableCell
                  {...cell.getCellProps()}
                  align="left"
                  sx={{ height: rowHeight + 'px', verticalAlign: 'middle' }}
                >
                  {cell.render('Cell')}
                </TableCell>
              ))}
              <TableCell align="right">
                <Button onClick={() => onAdd(row.original)} placeholder={t('page.staffs.staffsTable.header.action')}><PersonAdd /></Button>
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table >
  )
}

export default AddMedecinTable
